import { useDispatch } from "react-redux";
import { handleHrefRedirect, PAGE_CONSTANT, runAnalytic_v2 } from "../services/functions";
import { useNavigate } from "react-router-dom";
import { LOGOUT_PATH } from "../utility/path";
import { showAlert } from "../redux/slices/alertSlice";
import { assertAuth } from "../utility/authGuard";
import "../detailsUserInfoCard.css";
import { useMemo } from "react";
import { getRandomIconColors } from "../utility/colorUtils";
import { normalizeInstagramLink } from "../utility/normalizeLinks";
import { isClickBlocked, isWithinClosingTime } from "../utility/timingUtils";
import { StimingData, EtimingData } from "../utility/timingUtils";
import StatusBadge from "./StatusBadge";


const DetailsUserInfo = ({ hideMobile, detailsData, authData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Inside component — useMemo prevents re-randomizing on every render
  const categoryIconColors = useMemo(() => getRandomIconColors(), []);
  // professional's own working hours
  const isWithinUserHours = detailsData?.startTime && detailsData?.endTime ? isWithinClosingTime(detailsData?.startTime, detailsData?.endTime) 
  : false; // no hours set = always available

  const isUnavailable = !isWithinUserHours || isClickBlocked(); // ← single derived flag

   // ── Pre-compute time labels once ─────────────────────────
  const startLabel = useMemo(
    () => StimingData.find(t => t.id === detailsData?.startTime)?.name || "",
    [detailsData?.startTime]
  );
  const endLabel = useMemo(
    () => EtimingData.find(t => t.id === detailsData?.endTime)?.name || "",
    [detailsData?.endTime]
  );

  // ── Derived address string ────────────────────────────────
  const addressText = useMemo(() => [
    detailsData?.address,
    detailsData?.pincode,
    detailsData?.postOffice?.District,
    detailsData?.postOffice?.Division,
    detailsData?.postOffice?.State,
    detailsData?.postOffice?.Country,
  ].filter(Boolean).join(", "), [detailsData]);

// ── Social links ──────────────────────────────────────────
  const socialLinks = useMemo(() => [
    { link: normalizeInstagramLink(detailsData?.instalink), icon: "fa-instagram", cls: "uic-social-instagram" },
    { link: detailsData?.fblink,                           icon: "fa-facebook-f", cls: "uic-social-facebook"  },
  ].filter(item => item.link), [detailsData?.instalink, detailsData?.fblink]);


  const handlePhoneCall = () => {
    if (isUnavailable) return;

    const redirectPath = detailsData?.id ? `/details/${detailsData.id}` : null;
    if (!assertAuth(authData, dispatch, redirectPath)) return;

    runAnalytic_v2({ categoryToAdd: detailsData?.category?.[0]?.name, authData, dispatch, page: PAGE_CONSTANT.DETAILS_PAGE_CALL_CLICK, producer: detailsData?.id, searchCategoryId: detailsData?.category?.[0]?.id })
    if (window?.cordova && window?.cordova?.InAppBrowser) {
      cordova.InAppBrowser.open(`tel:${detailsData?.mobile}`, "_system");
    } else {
      window.location.href = "tel:" + detailsData?.mobile;
    }
  };


  return (
    <div className="d-flex flex-column text-break details-user-info-responsive uic-card">

       {/* Header */}
    <div className="uic-header">
      <div className="uic-avatar">
        {detailsData?.name?.charAt(0).toUpperCase()}
      </div>
      <div>
        <p className="uic-name">{detailsData?.name}</p>
        <StatusBadge isClosed={isUnavailable} />
      </div>
    </div>

      {/* Address Section */}
      {!hideMobile && (
        <>
        {/* Address */}
         <div className="uic-section">

          {addressText && (
              <div className="uic-row">
                <i className="fa fa-map-marker uic-icon" />
                <span className="uic-text">{addressText}</span>
              </div>
            )}

            {/* Phone Call */}
            <div className="uic-row">
              {(isUnavailable) ? (
                <span>
                  <i className="fa fa-phone uic-icon" style={{ color: "#9ca3af" }} />
                  <i className="fa fa-ban" style={{ fontSize: 10, color: "#ef4444" }} />
                </span>
              ) : (
                <i className="fa fa-phone uic-icon" />
              )}

              {isUnavailable ? (
                <div>
                  <span className="uic-unavailable__text">Currently unavailable</span>
                  {startLabel && (
                    <span className="uic-unavailable__time">Opens at {startLabel}</span>
                  )}
                </div>
              ) : (
                <a href="#" className="uic-link uic-name" onClick={handlePhoneCall}>
                  Call {detailsData?.name}
                </a>
              )}
            </div>
         </div>

          {/* Category */}
          {detailsData?.category?.[0] && (
             <div className="uic-section">
            <p className="uic-section-label">Profession</p>
            <div className="uic-row">
              <div className="uic-category-icon"  style={categoryIconColors}>
                <img
                  src={detailsData.category?.[0]?.photo || logo}
                  alt={detailsData.category?.[0]?.name}
                  className="uic-category-img"
                  onError={(e) => { e.target.src = logo; }}
                />
              </div>
              <span className="uic-category-name">
                {detailsData.category?.[0]?.name}
              </span>
            </div>
          </div>
          )}

          {/* About Me */}
          {detailsData?.aboutme && (
           <div className="uic-section">
            <p className="uic-section-label">About</p>
            <div className="uic-row">
              <i className="fa fa-info-circle uic-icon" />
              <span className="uic-text">{detailsData.aboutme}</span>
            </div>
          </div>
          )}

          {/* Working Hours */}
          {startLabel && endLabel && (
           <div className="uic-section">
            <p className="uic-section-label">Working Hours <i className="fa fa-clock-o uic-icon" /></p>
            <div className="uic-row">
              {/* <i className="fa fa-clock-o uic-icon" />
              <span className="uic-text">From: {StimingData.find(time => time.id === detailsData.startTime)?.name}&nbsp;&nbsp;To:{EtimingData.find(time => time.id === detailsData.endTime)?.name}</span> */}
              <div className="uic-wh-slot">
                <div className="uic-wh-tag">From</div>
                <div className="uic-wh-time">{startLabel}</div>
              </div>
              <i className="fa fa-arrow-right uic-icon" />
              <div className="uic-wh-slot">
                <div className="uic-wh-tag">To</div>
                <div className="uic-wh-time">{endLabel}</div>
              </div>
              <StatusBadge isClosed={isUnavailable} />
            </div>
          </div>
          )}

          {/* Social Links */}
          {socialLinks.length > 0 && (
             <div className="uic-section uic-social-section">
            <p className="uic-section-label">Follow</p>
            <div className="uic-social-links">
             {socialLinks.map((item, index) => (
                  <a
                    key={index}
                    href="#"
                    rel="noopener noreferrer"
                    className={`uic-social-btn ${item.cls}`}
                    onClick={() => handleHrefRedirect(item.link)}
                  >
                    <i className={`fa ${item.icon}`} />
                  </a>
              ))}
            </div>
          </div>
          )}
        </>
      )}
    </div>

  );
}

export default DetailsUserInfo;
